import type { ComponentProps } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { cn } from '@/lib/utils';

import { useCollapsedChips } from '@/hooks';

const FilterChip = (props: ComponentProps<typeof Badge>) => {
  const { className, ...badgeProps } = props;

  return (
    <Badge
      variant="chip"
      size="chip"
      className={cn('h-8 px-3', className)}
      {...badgeProps}
    />
  );
};

type FilterChipItem = { key: string; text: string };

type ListFilterChipsProps = { chips: FilterChipItem[] };

export const ListFilterChips = (props: ListFilterChipsProps) => {
  const { chips } = props;
  const { chipsRef, measureRef, visibleCount } = useCollapsedChips(
    chips.map((chip) => chip.text),
    chips.length > 0,
    { minWidthRatio: 1, minVisibleCount: 0 },
  );

  if (chips.length === 0) {
    return null;
  }

  const visibleChips = chips.slice(0, visibleCount);
  const hiddenChips = chips.slice(visibleCount);

  return (
    <div
      ref={chipsRef}
      className="animate-in fade-in-0 slide-in-from-left-1 relative flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden duration-200 group-data-[state=open]/filters:hidden"
    >
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute top-0 left-0 flex w-max gap-1.5"
      >
        {chips.map((chip) => (
          <FilterChip key={chip.key}>{chip.text}</FilterChip>
        ))}
        <FilterChip>+{chips.length}</FilterChip>
      </div>
      {visibleChips.map((chip) => (
        <FilterChip key={chip.key}>{chip.text}</FilterChip>
      ))}
      {hiddenChips.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <FilterChip
              tabIndex={0}
              className="shrink-0"
            >
              +{hiddenChips.length}
            </FilterChip>
          </TooltipTrigger>
          <TooltipContent className="flex flex-col gap-0.5">
            {hiddenChips.map((chip) => (
              <span key={chip.key}>{chip.text}</span>
            ))}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
